import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { ClockIcon } from '@heroicons/react/24/outline';

// Define keyframes for animations
const keyframes = `
  @keyframes pulse {
    0% { opacity: 0.8; }
    50% { opacity: 1; }
    100% { opacity: 0.8; }
  }
  
  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
`;

// Helper function to combine classes
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// App color constants matching the application's theme
const APP_COLORS = {
  primary: '#1976d2',        // Blue - Primary brand color
  primaryLight: '#42a5f5',   // Lighter shade of primary
  primaryDark: '#0d47a1',    // Darker shade of primary
  secondary: '#4caf50',      // Green - Secondary/Success color
  secondaryLight: '#81c784', // Lighter shade of secondary
  secondaryDark: '#388e3c',  // Darker shade of secondary
  warning: '#ff9800',        // Orange - Warning color
  warningLight: '#ffb74d',   // Lighter shade of warning
  error: '#f44336',          // Red - Error color
  gray: '#7f8c8d',           // Text secondary color
  grayLight: '#ecf0f1',      // Light gray for backgrounds
  grayDark: '#2c3e50',       // Dark gray for text
};

export default function UARSteps({ steps, onStepClick, showSteps = true }) {
  // Add global keyframes
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = keyframes;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle click on a step
  const handleStepClick = (step, index) => {
    // If custom click handler is provided, use it
    if (onStepClick) {
      onStepClick(step, index);
    } else if (step.href) {
      // Default behavior: navigate to href if provided
      window.location.href = step.href;
    } else if (step.scrollTo) {
      // Or scroll to element ID if provided
      const element = document.getElementById(step.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Calculate completion percentage
  const completedSteps = steps.filter(step => step.status === 'complete').length;
  const completionPercentage = (completedSteps / steps.length) * 100;
  
  return (
    <div className="w-full mb-3">
      {/* Minimalist Progress bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-gray-500">Progress ({Math.round(completionPercentage)}%)</div>
        <div className="text-xs font-medium text-gray-500">
          {completedSteps}/{steps.length} steps completed
        </div>
      </div>
      
      <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden mb-3">
        <div 
          className="h-full rounded-full" 
          style={{ 
            width: `${completionPercentage}%`,
            transition: 'width 0.5s ease-in-out',
            backgroundColor: APP_COLORS.primary
          }} 
        />
      </div>
      
      {/* Minimalist stepper */}
      {showSteps && (
        <nav aria-label="Progress" className="mt-1">
          <ol className="flex items-center justify-between px-4" style={{ margin: '0 -20px' }}>
            {steps.map((step, stepIdx) => {
              const isClickable = step.status === 'complete' || step.status === 'current';
              
              return (
                <li key={step.id} className="relative flex items-center" style={{ margin: '0 20px' }}>
                  {/* Step connector line */}
                  {stepIdx !== 0 && (
                    <div 
                      className="absolute left-0 right-0 -translate-x-[calc(100%+24px)] w-[calc(100%+24px)] h-[2px]" 
                      style={{ 
                        backgroundColor: step.status === 'upcoming' ? '#e0e0e0' : APP_COLORS.primary,
                        zIndex: 0
                      }}
                    />
                  )}
                  
                  {/* Step indicator dot */}
                  <button
                    onClick={isClickable ? () => handleStepClick(step, stepIdx) : undefined}
                    className={classNames(
                      "relative z-10 flex items-center justify-center group",
                      isClickable ? "cursor-pointer" : "cursor-default"
                    )}
                  >
                    {step.status === 'complete' ? (
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center" 
                        style={{ 
                          backgroundColor: APP_COLORS.secondary,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <CheckIcon className="w-4 h-4 text-white" aria-hidden="true" />
                      </div>
                    ) : step.status === 'current' ? (
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center" 
                        style={{ 
                          backgroundColor: APP_COLORS.primary,
                          boxShadow: '0 0 0 2px white, 0 0 0 4px ' + APP_COLORS.primaryLight,
                          animation: 'pulse 2s infinite ease-in-out',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <ClockIcon className="w-4 h-4 text-white" aria-hidden="true" />
                      </div>
                    ) : (
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200"
                        style={{ transition: 'all 0.3s ease' }}
                      >
                        <span className="text-xs text-gray-600 font-medium">{step.id}</span>
                      </div>
                    )}
                    
                    {/* Step name tooltip on hover */}
                    <div className="absolute top-full mt-10 left-1/2 -translate-x-1/2 hidden group-hover:block bg-white shadow-md rounded px-3 py-2 text-xs z-20 w-max">
                      <div className="font-medium mb-1">{step.name}</div>
                      <div className="text-gray-500">{step.description}</div>
                    </div>
                  </button>

                  {/* Step label below (visible only on larger screens) */}
                  <div className="hidden sm:block absolute top-full pt-2 left-1/2 -translate-x-1/2 text-center">
                    <span 
                      className="block text-xs font-medium" 
                      style={{
                        color: step.status === 'complete' ? APP_COLORS.secondaryDark : 
                              step.status === 'current' ? APP_COLORS.primaryDark :
                              APP_COLORS.gray,
                        maxWidth: '120px',
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textAlign: 'center',
                        lineHeight: '1.2'
                      }}
                    >
                      {step.name}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>
      )}
      
      {/* Show current step name on small screens */}
      <div className="sm:hidden mt-5 text-center">
        {steps.map(step => {
          if (step.status === 'current') {
            return (
              <div key={step.id} className="text-sm font-medium" style={{ color: APP_COLORS.primaryDark }}>
                Current: {step.name} - {step.description}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
